import type { ArticleBlocksList, ArticleData, CampaignStrategy } from '../entities/article';
import { Block, MoneyBlockType } from '@benzinga/content-manager';
import { runningClientSide } from '@benzinga/utils';
import { MetaProps } from '@benzinga/seo';
import { getSettings, getCampaignStrategySetting, getAllRefPartners } from '@benzinga/ads-utils';
import { splitElements } from './content';
import { getArticleCampaignQueryParams, getCampaignSessionReferrer, getArticleReferer } from './ads';
import type { ArticleBlock } from '../entities/article';

const insertAt = <T>(array: T[], position: number, value: T): T[] => {
  const copy = [...array];
  copy.splice(position, 0, value);
  return copy;
};

export const insertCampaignInBlocks = (
  articleBlocks: ArticleBlocksList[],
  campaignBlock: Block,
  position = 3,
): { index: number | null; modifiedBlocks: ArticleBlocksList[] } => {
  if (articleBlocks) {
    const elementsCount = articleBlocks.length;
    const adjustedPosition = elementsCount === 4 || elementsCount === 5 ? 2 : position;

    return { index: adjustedPosition, modifiedBlocks: insertAt(articleBlocks, adjustedPosition, campaignBlock) };
  } else {
    return { index: null, modifiedBlocks: articleBlocks };
  }
};

export const insertCampaign = (html: string | string[], stringToInsert: string, position = 3) => {
  if (Array.isArray(html)) {
    const elementsCount = html.length;
    const adjustedPosition = elementsCount === 4 || elementsCount === 5 ? 2 : position;

    return insertAt(html, adjustedPosition, stringToInsert);
  } else {
    const elementsArray = splitElements(html) ?? [html];
    const elementsCount = elementsArray.length;
    const adjustedPosition = elementsCount === 4 || elementsCount === 5 ? 2 : position;

    return insertAt(elementsArray, adjustedPosition, stringToInsert).join('');
  }
};

export const findPlaceholderPositionInParsedContent = (
  parsedContent: string[],
  startPositionIndex: number,
  insertCount: number,
) => {
  try {
    let currentInsertCount = 1;
    let isLastElementP = false;
    let placeholderPosition = startPositionIndex + 1;

    const isElementTag = (elementString: string) => {
      return elementString.indexOf('<') === 0;
    };

    const isElementP = (elementString: string) => {
      return elementString.indexOf('<p') === 0;
    };

    let canInsertPlaceholder: boolean | null = null;
    while (canInsertPlaceholder === null) {
      const element = parsedContent[placeholderPosition];
      if (element) {
        if (isElementTag(element)) {
          if (isElementP(element)) {
            if (isLastElementP && currentInsertCount > insertCount) {
              canInsertPlaceholder = true;
            } else {
              isLastElementP = true;
              currentInsertCount++;
              placeholderPosition++;
            }
          } else {
            isLastElementP = false;
            placeholderPosition++;
          }
        } else {
          placeholderPosition++;
        }
      } else {
        canInsertPlaceholder = false;
      }
    }

    return !canInsertPlaceholder ? placeholderPosition : false;
  } catch (error) {
    console.error('PLACEHOLDER FIND ERROR:', error);
    return false;
  }
};

export const findPlaceholderPlaceInBlocks = (
  parsedContent: ArticleBlock[] | MoneyBlockType[],
  startPositionIndex: number,
  insertCount: number,
): number | false => {
  try {
    let currentInsertCount = 1;
    let isLastElementP = false;
    let placeholderPosition = startPositionIndex + 1;

    const isElementTag = (element: ArticleBlock | MoneyBlockType) => {
      return element.nodeType === 1;
    };

    const isElementP = (element: ArticleBlock | MoneyBlockType) => {
      return element?.tag === 'p';
    };

    let canInsertPlaceholder: boolean | null = null;
    while (canInsertPlaceholder === null) {
      const element = parsedContent[placeholderPosition];
      if (element) {
        if (isElementTag(element)) {
          if (isElementP(element)) {
            if (isLastElementP && currentInsertCount > insertCount) {
              canInsertPlaceholder = true;
            } else {
              isLastElementP = true;
              currentInsertCount++;
              placeholderPosition++;
            }
          } else {
            isLastElementP = false;
            placeholderPosition++;
          }
        } else {
          placeholderPosition++;
        }
      } else {
        canInsertPlaceholder = false;
      }
    }

    return canInsertPlaceholder ? placeholderPosition : false;
  } catch (error) {
    console.error('PLACEHOLDER FIND ERROR:', error);
    return false;
  }
};

const getStringParam = (params: Record<string, unknown> | undefined, key: string): string | undefined => {
  if (!params) return undefined;
  const value = params[key];
  return typeof value === 'string' ? value : undefined;
};

export const getArticleCampaignStrategy = (
  queryParams: Record<string, unknown> | undefined,
  articleData?: ArticleData,
): CampaignStrategy => {
  if (articleData?.layout?.campaign_layout === 'custom') {
    return 'custom';
  }

  if (articleData?.meta?.wp_post_meta?.campaign_strategy === 'Custom') {
    return 'none';
  }

  if ((!queryParams || typeof queryParams !== 'object') && typeof articleData?.metaProps !== 'object') {
    return 'external';
  }

  const campaignStrategyParam = getStringParam(queryParams, 'campaigns_strategy');
  if (campaignStrategyParam) {
    return campaignStrategyParam as CampaignStrategy;
  }

  const utmSource = getStringParam(queryParams, 'utm_source');
  const settings = utmSource ? getSettings(utmSource.toLowerCase()) : null;
  if (settings) {
    return settings as CampaignStrategy;
  }

  const targetReferer = getCampaignSessionReferrer() || getArticleReferer(articleData?.metaProps);
  if (targetReferer) {
    let targetRefStrategy: string | null = '';
    const partnersRef = getAllRefPartners();

    partnersRef.forEach(partnerKey => {
      if (targetReferer.toLowerCase().includes(partnerKey)) {
        targetRefStrategy = getCampaignStrategySetting(partnerKey);
      }
    });

    if (targetRefStrategy) {
      return targetRefStrategy as CampaignStrategy;
    }
  }

  return 'external';
};

export const injectNodeIdInCampaignBlocks = (blocks: Block[], identifierName: string, ident: string) => {
  if (Array.isArray(blocks)) {
    blocks.forEach(block => {
      if (block.blockName === 'acf/google-ad-placement') {
        const blockGoogleAdId = block.attrs.data['slot_google_ad_id'];

        if (blockGoogleAdId?.includes(ident)) {
          return;
        }

        block.attrs.data['slot_google_ad_id'] = blockGoogleAdId
          ? `${blockGoogleAdId}-${ident}`
          : `${identifierName}-${ident}`;
      } else if (block.blockName === 'acf/investing-channel-block' || block.blockName === 'acf/investing-channel') {
        if (block?.attrs?.data['id']?.includes(ident)) {
          return;
        }

        block.attrs.data['id'] = `${block.attrs.data['id']}-${ident}`;
      } else if (block.blockName === 'acf/widget-container' && block.blocks) {
        block.blocks = injectNodeIdInCampaignBlocks(block.blocks, identifierName, ident);
      }
    });
  }

  return blocks;
};

export const performRegexesOnCampaignHTML = (campaign: string) => {
  let campaignHTML = campaign;
  const regexes = [
    {
      replaceValue: 'border="1"',
      searchValue: /border="[2-9]"/g,
    },
  ];
  regexes.forEach(regex => {
    campaignHTML = campaignHTML.replaceAll(regex.searchValue, regex.replaceValue);
  });
  return campaignHTML;
};
